import type { ReactNode } from 'react';
import type { FacilitySignal } from '../../services/api/facilityMetricsService';
import type {
    BreakdownEntry,
    SignalCardProps,
} from '../dashboard/SignalCard';

export type SignalVisual =
    | 'plain'
    | 'ring'
    | 'bar'
    | 'segments'
    | 'bars'
    | 'sparkline';

export interface SignalDisplayConfig {
    icon?: ReactNode;
    tooltip?: string;
    /** Explicit visual override. If omitted, the helper shape-detects. */
    visual?: SignalVisual;
    /** For sparkline visuals: how to format the hover tooltip value. */
    trendFormatter?: (value: number) => string;
    /** Optional formatter for the primary signal value. */
    valueFormatter?: (value: number) => string;
    /** Optional formatter for bars breakdown values. */
    breakdownValueFormatter?: (value: number) => string;
    /** Subtitle shown below the value. */
    subtitle?: string;
}

export function toSignalCardProps(
    signal: FacilitySignal,
    config: SignalDisplayConfig = {}
): SignalCardProps {
    const title = signal.label;
    const icon = config.icon;
    const tooltip = config.tooltip;
    const formatValue = config.valueFormatter ?? ((value: number) => value);

    const visual = config.visual ?? detectVisual(signal);

    switch (visual) {
        case 'ring': {
            const target = signal.target ?? 0;
            const pct =
                target > 0 ? Math.round((signal.current / target) * 100) : 0;
            return {
                title,
                icon,
                tooltip,
                value: `${pct}%`,
                visual: 'ring',
                progress: pct,
                ringCenterLabel: `${signal.current}/${target}`,
            };
        }
        case 'bars': {
            const breakdown: BreakdownEntry[] = signal.breakdown
                ? Object.entries(signal.breakdown).map(([label, value]) => ({
                      label,
                      value,
                  }))
                : [];
            return {
                title,
                icon,
                tooltip,
                value: formatValue(signal.current),
                subtitle: config.subtitle,
                visual: 'bars',
                breakdown,
                breakdownValueFormatter: config.breakdownValueFormatter,
            };
        }
        case 'sparkline': {
            return {
                title,
                icon,
                tooltip,
                value: formatValue(signal.current),
                subtitle: config.subtitle,
                visual: 'sparkline',
                trend: signal.trend ?? [],
                trendFormatter: config.trendFormatter,
            };
        }
        case 'segments': {
            const target = signal.target ?? 0;
            return {
                title,
                icon,
                tooltip,
                value: formatValue(signal.current),
                subtitle: buildTargetSubtitle(signal),
                visual: 'segments',
                filled: signal.current,
                total: target,
            };
        }
        case 'bar': {
            const target = signal.target ?? 0;
            const progress =
                target > 0 ? Math.round((signal.current / target) * 100) : 0;
            return {
                title,
                icon,
                tooltip,
                value: formatValue(signal.current),
                subtitle: buildTargetSubtitle(signal),
                visual: 'bar',
                progress,
            };
        }
        case 'plain':
        default:
            return {
                title,
                icon,
                tooltip,
                value: formatValue(signal.current),
                subtitle: config.subtitle,
            };
    }
}

function detectVisual(signal: FacilitySignal): SignalVisual {
    if (signal.displayFormat === 'percent' && signal.target) return 'ring';
    const hasDisplayedTarget =
        signal.target != null && signal.target > 0 && !!signal.targetLabel;
    if (hasDisplayedTarget) {
        return signal.target! <= 16 ? 'segments' : 'bar';
    }
    return 'plain';
}

function buildTargetSubtitle(signal: FacilitySignal): string | undefined {
    if (signal.target != null && signal.targetLabel) {
        return `${signal.current} / ${signal.target} ${signal.targetLabel}`;
    }
    return undefined;
}
